import React from 'react';
import { ArrowRight, FileText, Mail, MessageCircle, ShieldCheck, Wallet, Globe2, HelpCircle } from 'lucide-react';
import Footer from '../components/Footer';
import { CONTACT_EMAIL, SUPPORT_EMAIL_URL, WEB3_NAMESPACE } from '../mock';

const contactCards = [
  {
    icon: Mail,
    title: 'Email Support',
    body: 'Send issuer admission, documented-right, evidence vault, wallet settlement, or compliant asset tokenization questions to the Tangibl3Net team.',
    action: 'Send Email',
    href: SUPPORT_EMAIL_URL,
  },
  {
    icon: FileText,
    title: 'Issuer File Review',
    body: 'Use this path for legal instrument intake, required documents, product lane questions, beneficiary-ledger review, and source-of-truth review.',
    action: 'Start Admission',
    href: '/#wizard',
  },
  {
    icon: MessageCircle,
    title: 'Implementation Questions',
    body: 'For Polygon, .tangiblenet identity, wallet settlement, transfer controls, and certificate workflow support, contact the team before enabling live minting.',
    action: 'Contact Team',
    href: SUPPORT_EMAIL_URL,
  },
];

const faqItems = [
  {
    question: 'What is Tangibl3Net?',
    answer: 'Tangibl3Net helps issuers, fiduciaries, producers, NGOs, and asset sponsors convert documented rights into controlled digital certificates with wallet settlement, beneficiary ledgers, transfer restrictions, and audit-ready files, for compliant asset tokenization. The public website is tangibl3.net.',
  },
  {
    question: `What is ${WEB3_NAMESPACE}?`,
    answer: `${WEB3_NAMESPACE} is the Polygon Web3 TLD / namespace for issuer identities, certificate program pointers, wallets, and public verification records.`,
  },
  {
    question: 'Is the .tangiblenet name the legal instrument?',
    answer: 'No. A .tangiblenet identity is an identity pointer and registry object. It is not the legal instrument, not proof of asset ownership, and not a guarantee of compliance, redemption, liquidity, or asset quality.',
  },
  {
    question: 'Does Tangibl3Net provide legal or regulatory advice?',
    answer: 'No. Tangibl3Net provides platform operations, recordkeeping, wallet-infrastructure integration, compliance workflow tooling, beneficiary ledgers, and controlled transaction administration. Human legal and regulatory review remains separate.',
  },
  {
    question: 'Is ERC-1404 the Tangibl3Net standard?',
    answer: 'No. Tangibl3Net should not market ERC-1404 as the product standard. Restricted-transfer compatible logic may be used where a certificate program requires permissions, whitelist checks, or reason-code controls on Polygon.',
  },
  {
    question: 'Why is the support page frontend-only?',
    answer: 'This support center is a frontend route. Email links work now, while ticketing, chat, CRM routing, and backend support workflows can be connected later.',
  },
];

const SupportPage = () => (
  <>
    <main className="pt-32 pb-20">
      <section className="relative overflow-hidden border-b border-border pb-20">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="section-label mb-4">Support Center</div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.05]">
              How can we <span className="gradient-text">help?</span>
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Choose how you want to reach Tangibl3Net for documented-right review, issuer admission, certificate configuration, wallet settlement, {WEB3_NAMESPACE} identity, evidence vault, or beneficiary-ledger questions.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-3 gap-5">
          {contactCards.map((card) => (
            <article key={card.title} className="rounded-2xl border border-border bg-card p-7 shadow-sm hover:shadow-lg hover:border-primary/40 transition-all">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                <card.icon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="font-display text-xl font-bold text-foreground">{card.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
              <a href={card.href} className="mt-6 inline-flex items-center gap-2 font-semibold text-primary hover:text-primary/80">
                {card.action} <ArrowRight className="w-4 h-4" />
              </a>
            </article>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[0.75fr_1.25fr] gap-12 items-start">
          <div>
            <div className="section-label mb-4">FAQ</div>
            <h2 className="font-display text-4xl font-bold tracking-tight text-foreground">Frequently Asked Questions</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              This support page is intentionally focused on compliant asset tokenization through issuer-file operations, documented-right review, role separation, Polygon identity, and controlled certificate workflows.
            </p>
            <div className="mt-8 grid gap-3">
              <div className="rounded-xl border border-border bg-muted/40 p-4 flex gap-3">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">Human review remains required before any certificate program is activated.</span>
              </div>
              <div className="rounded-xl border border-border bg-muted/40 p-4 flex gap-3">
                <Globe2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">The Tangibl3Net website is tangibl3.net. The .tangiblenet name is the Polygon identity namespace.</span>
              </div>
              <div className="rounded-xl border border-border bg-muted/40 p-4 flex gap-3">
                <Wallet className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">Live wallet and TLD minting remain disabled until contracts are configured.</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <details key={item.question} className="group rounded-2xl border border-border bg-card p-6 open:border-primary/40 open:shadow-sm">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-5">
                  <span className="font-display text-lg font-semibold text-foreground">{item.question}</span>
                  <HelpCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-8 lg:p-10">
            <div className="section-label mb-4">Operational Notice</div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Tangibl3Net provides issuer workspace software, wallet-infrastructure integration, recordkeeping, certificate workflow tools, beneficiary ledgers, and controlled transaction administration for compliant asset tokenization. Tangibl3Net does not act as issuer, broker, dealer, exchange, custodian, trustee, asset manager, guarantor, or legal advisor unless separately licensed or contractually appointed.
            </p>
            <a href={`mailto:${CONTACT_EMAIL}`} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3.5 font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
              Contact {CONTACT_EMAIL} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default SupportPage;
